import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { labReportsApi, type LabReportScope } from "@/api/labReports";
import { useChainReportHospitalScope } from "@/hooks/useChainReportHospitalScope";
import { ReportHospitalFilter } from "@/components/reports/ReportHospitalFilter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Download } from "lucide-react";
import { toast } from "sonner";

function isoStartOfDay(d: string) {
  return new Date(`${d}T00:00:00.000Z`).toISOString();
}
function isoEndOfDay(d: string) {
  return new Date(`${d}T23:59:59.999Z`).toISOString();
}

export default function LabReportsPage() {
  const {
    showHospitalFilter,
    hospitalOptions,
    selectedHospitalId,
    setSelectedHospitalId,
    reportHospitalId,
    isSuperAdmin,
  } = useChainReportHospitalScope({ defaultScope: "consolidated" });

  const today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [scope, setScope] = useState<LabReportScope>("ALL");

  const fromIso = useMemo(() => isoStartOfDay(fromDate), [fromDate]);
  const toIso = useMemo(() => isoEndOfDay(toDate), [toDate]);

  const { data: summaryRes, isLoading } = useQuery({
    queryKey: ["lab-reports-summary", fromIso, toIso, scope, reportHospitalId ?? "self"],
    queryFn: () => labReportsApi.summary({ from: fromIso, to: toIso, scope, hospitalId: reportHospitalId }),
    enabled: !!fromIso && !!toIso,
  });

  const summary = summaryRes?.data?.data;

  const { data: revenueCutsRes } = useQuery({
    queryKey: ["lab-reports-revenue-cuts", fromIso, toIso, scope, reportHospitalId ?? "self"],
    queryFn: () => labReportsApi.revenueCuts({ from: fromIso, to: toIso, scope, hospitalId: reportHospitalId }),
    enabled: !!fromIso && !!toIso,
  });
  const revenueCutsRows = (revenueCutsRes as any)?.data?.data?.rows as any[] | undefined;

  const { data: mostUsedRes } = useQuery({
    queryKey: ["lab-reports-tests-most-used", fromIso, toIso, scope, reportHospitalId ?? "self"],
    queryFn: () => labReportsApi.testsMostUsed({ from: fromIso, to: toIso, scope, hospitalId: reportHospitalId }),
    enabled: !!fromIso && !!toIso,
  });
  const mostUsedRows = (mostUsedRes as any)?.data?.data?.rows as any[] | undefined;

  const { data: vendorWiseRes } = useQuery({
    queryKey: ["lab-reports-vendor-wise", fromIso, toIso, scope, reportHospitalId ?? "self"],
    queryFn: () => labReportsApi.vendorWise({ from: fromIso, to: toIso, scope, hospitalId: reportHospitalId }),
    enabled: !!fromIso && !!toIso,
  });
  const vendorWiseRows = (vendorWiseRes as any)?.data?.data?.rows as any[] | undefined;

  const downloadSummaryCsv = async () => {
    try {
      const res = await labReportsApi.summaryCsv({ from: fromIso, to: toIso, scope, hospitalId: reportHospitalId });
      const blob = res.data as unknown as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lab-summary-${fromDate}-to-${toDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to download CSV");
    }
  };

  const downloadPatientWiseCsv = async () => {
    try {
      const res = await labReportsApi.patientWiseCsv({ from: fromIso, to: toIso, scope, hospitalId: reportHospitalId });
      const blob = res.data as unknown as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lab-patient-wise-${fromDate}-to-${toDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to download CSV");
    }
  };

  const downloadMostUsedCsv = async () => {
    try {
      const res = await labReportsApi.testsMostUsedCsv({ from: fromIso, to: toIso, scope, hospitalId: reportHospitalId });
      const blob = res.data as unknown as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lab-tests-most-used-${fromDate}-to-${toDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to download CSV");
    }
  };

  const downloadVendorWiseCsv = async () => {
    try {
      const res = await labReportsApi.vendorWiseCsv({ from: fromIso, to: toIso, scope, hospitalId: reportHospitalId });
      const blob = res.data as unknown as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lab-vendor-wise-${fromDate}-to-${toDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to download CSV");
    }
  };

  const downloadRevenueCutsCsv = async () => {
    try {
      const res = await labReportsApi.revenueCutsCsv({ from: fromIso, to: toIso, scope, hospitalId: reportHospitalId });
      const blob = res.data as unknown as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lab-revenue-cuts-${fromDate}-to-${toDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to download CSV");
    }
  };

  return (
    <div className="thms-page-shell">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Lab Reports</h1>
          <p className="text-sm text-muted-foreground">OPD / IPD / Daycare lab analytics with vendor costs</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadRevenueCutsCsv} className="gap-2">
            <Download className="h-4 w-4" /> Revenue cuts CSV
          </Button>
          <Button variant="outline" onClick={downloadVendorWiseCsv} className="gap-2">
            <Download className="h-4 w-4" /> Vendor-wise CSV
          </Button>
          <Button variant="outline" onClick={downloadMostUsedCsv} className="gap-2">
            <Download className="h-4 w-4" /> Most used tests CSV
          </Button>
          <Button variant="outline" onClick={downloadPatientWiseCsv} className="gap-2">
            <Download className="h-4 w-4" /> Patient-wise CSV
          </Button>
          <Button variant="outline" onClick={downloadSummaryCsv} className="gap-2">
            <Download className="h-4 w-4" /> Summary CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <div className="text-sm font-medium mb-1">From</div>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div>
            <div className="text-sm font-medium mb-1">To</div>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <div>
            <div className="text-sm font-medium mb-1">Scope</div>
            <Select value={scope} onValueChange={(v: any) => setScope(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All</SelectItem>
                <SelectItem value="OPD">OPD</SelectItem>
                <SelectItem value="IPD">IPD</SelectItem>
                <SelectItem value="DAYCARE">Daycare</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {showHospitalFilter ? (
            <ReportHospitalFilter
              hospitals={hospitalOptions}
              value={selectedHospitalId}
              onChange={setSelectedHospitalId}
              isSuperAdmin={isSuperAdmin}
            />
          ) : (
            <div />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Summary (Patient billed vs Vendor cost)</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : !summary ? (
            <div className="text-sm text-muted-foreground">No data</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(["opd", "daycare", "ipd"] as const).map((k) => {
                const billed = Number(summary?.[k]?.billed ?? 0);
                const vendorCost = Number(summary?.[k]?.vendorCost ?? 0);
                const margin = billed - vendorCost;
                return (
                  <Card key={k} className="shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base uppercase">{k}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      <div className="text-sm">Billed: <span className="font-medium">₹{billed.toFixed(2)}</span></div>
                      <div className="text-sm">Vendor cost: <span className="font-medium">₹{vendorCost.toFixed(2)}</span></div>
                      <div className="text-sm">Margin: <span className="font-medium">₹{margin.toFixed(2)}</span></div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Revenue cuts (Internal vs External)</CardTitle>
        </CardHeader>
        <CardContent>
          {!revenueCutsRows?.length ? (
            <div className="text-sm text-muted-foreground">No data</div>
          ) : (
            <div className="w-full overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4">Scope</th>
                    <th className="text-right py-2 pr-4">Billed (Internal)</th>
                    <th className="text-right py-2 pr-4">Billed (External)</th>
                    <th className="text-right py-2 pr-4">Vendor cost (External)</th>
                    <th className="text-right py-2 pr-4">Total margin</th>
                  </tr>
                </thead>
                <tbody>
                  {revenueCutsRows.map((r) => (
                    <tr key={r.scope} className="border-b">
                      <td className="py-2 pr-4">{r.scope}</td>
                      <td className="py-2 pr-4 text-right">₹{Number(r.billedInternal ?? 0).toFixed(2)}</td>
                      <td className="py-2 pr-4 text-right">₹{Number(r.billedExternal ?? 0).toFixed(2)}</td>
                      <td className="py-2 pr-4 text-right">₹{Number(r.vendorCostExternal ?? 0).toFixed(2)}</td>
                      <td className="py-2 pr-4 text-right">₹{Number(r.totalMargin ?? 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Most used tests (Top 10)</CardTitle>
        </CardHeader>
        <CardContent>
          {!mostUsedRows?.length ? (
            <div className="text-sm text-muted-foreground">No data</div>
          ) : (
            <div className="w-full overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4">Scope</th>
                    <th className="text-left py-2 pr-4">Test</th>
                    <th className="text-right py-2 pr-4">Count</th>
                    <th className="text-right py-2 pr-4">Billed</th>
                    <th className="text-right py-2 pr-4">Vendor cost</th>
                    <th className="text-right py-2 pr-4">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {mostUsedRows.slice(0, 10).map((r, idx) => (
                    <tr key={`${r.scope}-${r.testCode ?? r.testName ?? idx}`} className="border-b">
                      <td className="py-2 pr-4">{r.scope}</td>
                      <td className="py-2 pr-4">
                        <div className="font-medium">{r.testName || "—"}</div>
                        <div className="text-xs text-muted-foreground">{r.testCode || ""}</div>
                      </td>
                      <td className="py-2 pr-4 text-right">{Number(r.testCount ?? 0)}</td>
                      <td className="py-2 pr-4 text-right">₹{Number(r.billed ?? 0).toFixed(2)}</td>
                      <td className="py-2 pr-4 text-right">₹{Number(r.vendorCost ?? 0).toFixed(2)}</td>
                      <td className="py-2 pr-4 text-right">₹{Number(r.margin ?? 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Vendor-wise (External) (Top 10)</CardTitle>
        </CardHeader>
        <CardContent>
          {!vendorWiseRows?.length ? (
            <div className="text-sm text-muted-foreground">No data</div>
          ) : (
            <div className="w-full overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4">Scope</th>
                    <th className="text-left py-2 pr-4">Vendor</th>
                    <th className="text-right py-2 pr-4">Patients</th>
                    <th className="text-right py-2 pr-4">Tests</th>
                    <th className="text-right py-2 pr-4">Billed</th>
                    <th className="text-right py-2 pr-4">Vendor cost</th>
                    <th className="text-right py-2 pr-4">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {vendorWiseRows.slice(0, 10).map((r, idx) => (
                    <tr key={`${r.scope}-${r.vendorName ?? idx}`} className="border-b">
                      <td className="py-2 pr-4">{r.scope}</td>
                      <td className="py-2 pr-4">{r.vendorName || "—"}</td>
                      <td className="py-2 pr-4 text-right">{Number(r.patientsCount ?? 0)}</td>
                      <td className="py-2 pr-4 text-right">{Number(r.testsCount ?? 0)}</td>
                      <td className="py-2 pr-4 text-right">₹{Number(r.billed ?? 0).toFixed(2)}</td>
                      <td className="py-2 pr-4 text-right">₹{Number(r.vendorCost ?? 0).toFixed(2)}</td>
                      <td className="py-2 pr-4 text-right">₹{Number(r.margin ?? 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

